import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "@/App.css";
import { CartProvider } from "./context/CartContext";
import { useSmoothScroll } from "./hooks/useSmoothScroll";
import { Header } from "./components/Header";
import { Hero } from "./components/Hero";
import { BrandMarquee } from "./components/BrandMarquee";
import { FlashDeals } from "./components/FlashDeals";
import { Manifesto } from "./components/Manifesto";
import { Categories } from "./components/Categories";
import { ShopSection } from "./components/ShopSection";
import { SubscriptionBox } from "./components/SubscriptionBox";
import { RecurringList } from "./components/RecurringList";
import { Footer } from "./components/Footer";
import { CartDrawer } from "./components/CartDrawer";
import { ProductModal } from "./components/ProductModal";
import { scrollToId } from "./lib/scroll";
import { flashInfo } from "./lib/flash";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AuthCallback } from "./components/AuthCallback";
import LoginPage from "./pages/LoginPage";
import AccountPage from "./pages/AccountPage";
import AdminPage from "./pages/AdminPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";
import LegalPage from "./pages/LegalPage";
import { Toaster } from "sonner";
import { InstallPrompt } from "./components/InstallPrompt";
import { SplashScreen } from "./components/SplashScreen";
import { StoreSettingsProvider, useStoreSettings } from "./context/StoreSettingsContext";

const VetrinaBanner = () => (
  <div className="safe-pt border-b border-sage/25 bg-sage/10 px-4 py-2.5 text-center" data-testid="vetrina-banner">
    <p className="text-xs font-medium text-ink md:text-sm">
      <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-sage align-middle" />
      NEED apre a breve: puoi esplorare il catalogo, gli acquisti saranno attivi tra poco
    </p>
  </div>
);

const AdminTestBanner = () => (
  <div className="safe-pt border-b border-amber-500/40 bg-amber-100 px-4 py-2.5 text-center" data-testid="admin-test-banner">
    <p className="text-xs font-medium text-ink md:text-sm">
      <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-amber-500 align-middle" />
      Modalità prova admin: i clienti vedono la vetrina, tu puoi fare acquisti di test
    </p>
  </div>
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Store() {
  useSmoothScroll();
  const { shopEnabled } = useStoreSettings();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios.get(`${API}/categories`).then((r) => setCategories(r.data)).catch(console.error);
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(console.error);
    const hash = window.location.hash?.replace("#", "");
    if (hash && !hash.includes("session_id")) {
      setTimeout(() => scrollToId(hash), 400);
    }
  }, []);

  const filtered = useMemo(
    () => (activeCategory === "all" ? products : products.filter((p) => p.category === activeCategory)),
    [products, activeCategory]
  );

  const recurring = useMemo(() => products.filter((p) => p.recurring), [products]);

  const flashEnd = useMemo(() => {
    const ends = products.map((p) => flashInfo(p)).filter((f) => f.active).map((f) => f.end);
    return ends.length ? Math.min(...ends) : null;
  }, [products]);

  const activeLabel = activeCategory === "all"
    ? "Tutti gli essenziali"
    : categories.find((c) => c.slug === activeCategory)?.label || "Prodotti";

  const selectCategory = (slug) => {
    setActiveCategory(slug);
    setTimeout(() => scrollToId("griglia"), 80);
  };

  return (
    <div className="App">
      <Header />
      {!shopEnabled && (user?.role === "admin" ? <AdminTestBanner /> : <VetrinaBanner />)}
      <main>
        <Hero flashEnd={flashEnd} />
        <BrandMarquee />
        <FlashDeals products={products} onOpen={setSelected} />
        <Manifesto />
        <Categories categories={categories} activeCategory={activeCategory} onSelect={selectCategory} />
        <ShopSection
          categories={categories}
          products={filtered}
          activeCategory={activeCategory}
          activeLabel={activeLabel}
          onSelect={setActiveCategory}
          onOpen={setSelected}
        />
        <SubscriptionBox boxProducts={recurring.slice(0, 6)} />
        <RecurringList recurringProducts={recurring} />
      </main>
      <Footer />
      <CartDrawer />
      <ProductModal product={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Store />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/account" element={<AccountPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />
      <Route path="/termini" element={<LegalPage doc="termini" />} />
      <Route path="/privacy" element={<LegalPage doc="privacy" />} />
      <Route path="/cookie" element={<LegalPage doc="cookie" />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <StoreSettingsProvider>
        <CartProvider>
        <BrowserRouter>
          <AppRouter />
          <SplashScreen />
          <InstallPrompt />
          <Toaster position="top-center" richColors offset="calc(env(safe-area-inset-top, 0px) + 14px)" />
        </BrowserRouter>
        </CartProvider>
      </StoreSettingsProvider>
    </AuthProvider>
  );
}

export default App;
